import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import {
  HERO_YOUTUBE_VIDEO_URL,
  HERO_SLIDE_ONE_URL,
  HERO_SLIDE_TWO_URL,
  HERO_SLIDE_THREE_URL,
} from "@/config/assets";

const SLIDES = [HERO_SLIDE_ONE_URL, HERO_SLIDE_TWO_URL, HERO_SLIDE_THREE_URL];

const getYouTubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?.*v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
};

export const Hero = () => {
  const [slide, setSlide] = useState(0);
  const videoId = getYouTubeId(HERO_YOUTUBE_VIDEO_URL);

  useEffect(() => {
    if (videoId) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 6000);
    return () => clearInterval(t);
  }, [videoId]);

  return (
    <section id="hero" data-testid="hero-section" className="relative h-screen min-h-[640px] overflow-hidden">
      {videoId ? (
        <div className="absolute inset-0 pointer-events-none">
          <iframe
            data-testid="hero-youtube-video"
            title="Leelarav Greens venue video"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] min-w-full h-[56.25vw] min-h-full"
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
            allow="autoplay; encrypted-media"
            frameBorder="0"
          />
        </div>
      ) : (
        SLIDES.map((url, i) => (
          <motion.div
            key={url}
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${url})` }}
            initial={false}
            animate={{ opacity: slide === i ? 1 : 0, scale: slide === i ? 1.06 : 1 }}
            transition={{ opacity: { duration: 1.8 }, scale: { duration: 7, ease: "linear" } }}
          />
        ))
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-black/20" />

      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-center">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }}>
          <p className="text-[#D4AF37] uppercase tracking-[0.45em] text-xs md:text-sm mb-6">Leelarav Greens · Est. 2022</p>
          <h1 className="font-serif-luxe text-5xl md:text-6xl lg:text-7xl font-medium text-[#FAF9F6] leading-[1.05] max-w-4xl">
            Where Nature Meets <span className="italic text-[#F3E5AB]">Grandeur.</span>
          </h1>
          <p className="mt-8 text-[#FAF9F6]/85 text-base md:text-lg max-w-2xl leading-relaxed font-light">
            Since 2022, Gujarat's Premier Luxury Venue for Weddings, Corporate Galas, and Elite Celebrations.
          </p>
          <a
            href="#contact"
            data-testid="hero-schedule-tour-btn"
            className="gold-btn inline-block mt-12 px-10 py-4 rounded-sm text-sm font-semibold tracking-[0.2em] uppercase"
          >
            Schedule a Private Tour
          </a>
        </motion.div>
      </div>

      <motion.a
        href="#about"
        data-testid="hero-scroll-indicator"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#D4AF37] z-10"
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        aria-label="Scroll down"
      >
        <ChevronDown size={30} />
      </motion.a>

      {!videoId && (
        <div className="absolute bottom-8 right-8 z-10 hidden md:flex gap-3">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              data-testid={`hero-slide-dot-${i}`}
              onClick={() => setSlide(i)}
              className={`h-[3px] transition-[width,background-color] duration-500 ${slide === i ? "w-10 bg-[#D4AF37]" : "w-5 bg-white/40"}`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};
